import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Button, message, Spin, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  type GroupMemberAssignmentPayload,
  useEssenceAddGroupMember,
  useEssenceCreateGroupMember,
  useEssenceGetEnrichedDetails,
  useEssenceRemoveGroupMember,
  useEssenceUpdateCustomer,
} from '../api/essence-customer-api';
import type { CustomerUpdateDTO, GroupMemberDTO } from '@/shared/api/model';
import { useGetCategoryById } from '@/shared/api/client/customer-category-controller';
import CustomerDetailView from '../components/CustomerDetailView';
import CustomerEditForm from '../components/CustomerEditForm';
import AttachExistingMemberModal from '../components/AttachExistingMemberModal';
import GroupMemberModal from '@/modules/customer-onboarding/components/GroupMemberModal';
import type { GroupMemberData } from '@/modules/customer-onboarding/types';
import '../../customer-onboarding/styles/onboarding.css';

const { Content } = Layout;

const CustomerDetailViewPage: React.FC = () => {
  const { branch, custNo } = useParams<{ branch: string; custNo: string }>();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [memberModalVisible, setMemberModalVisible] = useState(false);
  const [newMemberModalVisible, setNewMemberModalVisible] = useState(false);

  // Fetch customer details
  const {
    data: customer,
    isLoading: loading,
    error,
    refetch,
  } = useEssenceGetEnrichedDetails(branch!, custNo!, {
    query: {
      enabled: !!(branch && custNo),
    },
  });

   const normalizedCustomerType = useMemo(() => {
     const explicitType = customer?.customerType?.toUpperCase?.();
     if (explicitType) {
       return explicitType;
     }

     switch ((customer?.customerData?.custType || '').toUpperCase()) {
       case 'GRP':
         return 'GROUP';
       case 'MEM':
         return 'MEMBER';
       case 'IND':
         return 'INDIVIDUAL';
       case 'BUS':
         return 'BUSINESS';
       default:
         return '';
     }
   }, [customer]);

   const isGroupCustomer = normalizedCustomerType === 'GROUP';

   const { data: customerCategory } = useGetCategoryById(Number(customer?.categoryId), {
     query: {
       enabled: !!customer?.categoryId && isGroupCustomer,
     },
   });

   const memberTemplateId = customerCategory?.parentCategoryId ?? null;

  // Update customer mutation
  const updateMutation = useEssenceUpdateCustomer({
    mutation: {
      onSuccess: () => {
        setEditMode(false);
        message.success('Customer updated successfully');
        refetch();
      },
      onError: () => {
        message.error('Failed to update customer');
      },
    },
  });

   const addMemberMutation = useEssenceAddGroupMember({
     mutation: {
       onSuccess: () => {
         setMemberModalVisible(false);
         message.success('Individual converted to group member successfully');
         refetch();
       },
       onError: (error) => {
         message.error(error instanceof Error ? error.message : 'Failed to add group member');
       },
     },
   });

   const createMemberMutation = useEssenceCreateGroupMember({
     mutation: {
       onSuccess: () => {
         setNewMemberModalVisible(false);
         message.success('New group member created successfully');
         refetch();
       },
       onError: (error) => {
         message.error(error instanceof Error ? error.message : 'Failed to create group member');
       },
     },
   });

   const removeMemberMutation = useEssenceRemoveGroupMember({
     mutation: {
       onSuccess: () => {
         message.success('Group member converted to individual successfully');
         refetch();
       },
       onError: (error) => {
         message.error(error instanceof Error ? error.message : 'Failed to remove group member');
       },
     },
   });

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const handleSave = async (updatePayload: CustomerUpdateDTO) => {
    updateMutation.mutate({ data: updatePayload });
  };

  const handleViewCustomer = (targetBranch: string, targetCustNo: string) => {
    navigate(`/customers/manage/${targetBranch}/${targetCustNo}`);
  };

   const handleOpenAddMember = () => {
     setMemberModalVisible(true);
   };

   const handleCloseAddMember = () => {
     setMemberModalVisible(false);
   };

   const handleOpenCreateMember = () => {
     if (!memberTemplateId) {
       message.warning('This group category is missing a member template configuration');
       return;
     }
     setNewMemberModalVisible(true);
   };

   const handleCloseCreateMember = () => {
     setNewMemberModalVisible(false);
   };

   const handleSaveMember = (payload: GroupMemberAssignmentPayload) => {
     if (!branch || !custNo) {
       message.error('Unable to add member: missing group information');
       return;
     }

     addMemberMutation.mutate({
       branch,
       custNo,
       data: {
         ...payload,
         memberTemplateId,
       },
     });
   };

   const handleCreateMember = (member: GroupMemberData) => {
     if (!branch || !custNo || !memberTemplateId) {
       message.error('Unable to create member: missing group or member template information');
       return;
     }

     const payload: GroupMemberDTO = {
       id: member.id,
       isLeader: member.isLeader,
       role: member.role,
       data: member.data,
       products: member.products,
       locations: member.locations,
       documents: member.documents,
       customerData: member.customerData,
       custInfoData: member.custInfoData,
     };

     createMemberMutation.mutate({
       branch,
       custNo,
       memberTemplateId,
       data: payload,
     });
   };

   const handleRemoveMember = (memberCustNo: string) => {
     if (!branch || !custNo) {
       message.error('Unable to remove member: missing group information');
       return;
     }

     removeMemberMutation.mutate({
       branch,
       custNo,
       memberCustNo,
     });
   };

  if (loading) {
    return (
      <Layout>
        <Content style={{ padding: '24px', textAlign: 'center' }}>
          <Spin size="large" />
          <p>Loading customer details...</p>
        </Content>
      </Layout>
    );
  }

  if (error || !customer) {
    return (
      <Layout>
        <Content style={{ padding: '24px' }}>
          <Alert
            message="Customer Not Found"
            description="The requested customer could not be found."
            type="error"
            showIcon
            action={
              <Button onClick={() => navigate('/customers/manage')}>
                Back to Search
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customers/manage')}
          style={{ marginBottom: 16 }}
        >
          Back to Search
        </Button>

        {editMode ? (
          <CustomerEditForm
            customer={customer}
            onSave={handleSave}
            onCancel={handleCancelEdit}
            loading={updateMutation.isPending}
          />
        ) : (
          <CustomerDetailView
            customer={customer}
            onEdit={handleEdit}
            onViewCustomer={handleViewCustomer}
            onAddMember={isGroupCustomer ? handleOpenAddMember : undefined}
            onCreateMember={isGroupCustomer ? handleOpenCreateMember : undefined}
            onRemoveMember={isGroupCustomer ? handleRemoveMember : undefined}
            memberActionLoading={addMemberMutation.isPending || createMemberMutation.isPending || removeMemberMutation.isPending}
            canAddMember
            loading={loading}
          />
        )}

       {isGroupCustomer && (
         <AttachExistingMemberModal
           visible={memberModalVisible}
           branch={branch!}
           groupCustNo={custNo!}
           loading={addMemberMutation.isPending}
           onSubmit={handleSaveMember}
           onCancel={handleCloseAddMember}
         />
       )}

       {isGroupCustomer && (
         <GroupMemberModal
           visible={newMemberModalVisible}
           memberTemplateId={memberTemplateId}
           onSave={handleCreateMember}
           onCancel={handleCloseCreateMember}
         />
       )}
      </Content>
    </Layout>
  );
};

export default CustomerDetailViewPage;
